import clsx from 'clsx';
import React from 'react';

import { SecondaryButton } from '@components/Button/variants';
import IconError from '@components/Images/Rebranding/IconError';
import IconPlus from '@components/Images/Rebranding/IconPlus';
import { PaymentMethod } from '@utils/backend-api/stripe';
import { CARD_PAYMENT_TYPE } from '@utils/constants';
import { getImgixSrc } from '@utils/images';

interface PaymentMethodHeaderProps {
  isExpanded: boolean;
  numberOfPaymentMethods: number;
  paymentMethod: PaymentMethod;
  setIsExpanded: (isExpanded: boolean) => void;
}

const PaymentMethodHeader = ({
  isExpanded,
  numberOfPaymentMethods,
  paymentMethod,
  setIsExpanded,
}: PaymentMethodHeaderProps) => {
  const { card, isPrimary, paymentType } = paymentMethod;
  const paymentMethodBrand = card?.brand || paymentType;
  const isCardPaymentType = paymentType === CARD_PAYMENT_TYPE;

  const isExpired = (expMonth: number, expYear: number) => {
    const now = new Date();

    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    return expYear < currentYear || (expYear === currentYear && expMonth < currentMonth);
  };

  return (
    <div className="flex justify-between">
      <div className="flex gap-24 font-suisse-screen text-headline-alt-sm font-light">
        <img
          src={getImgixSrc({
            path: `update_payment/${paymentMethodBrand}.svg`,
            isEnvDependent: false,
          })}
          alt={paymentMethodBrand}
          className="w-40 self-start"
        />
        <div className="flex flex-col">
          <div className="flex justify-between gap-24">
            {isCardPaymentType && <div className="font-normal">***** {card.last4}</div>}
            {isPrimary && (
              <div className="hidden self-start bg-grayscale-beige px-8 py-4 font-suisse-intl-mono text-overline-sm sm:block">
                <span>Primary</span>
              </div>
            )}
            {isCardPaymentType && isExpired(card.expMonth, card.expYear) && (
              <div className="hidden items-center text-button-sm text-primary-magenta sm:flex">
                <IconError className="mr-8 h-20 w-20" />
                <span>Expired</span>
              </div>
            )}
          </div>

          {isCardPaymentType && (
            <div className="text-left text-byline-lg font-normal text-grayscale-gray">
              Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
            </div>
          )}
          {isPrimary && (
            <div
              className={clsx(
                'block self-start bg-grayscale-beige px-8 py-4 font-suisse-intl-mono text-overline-sm sm:hidden',
                {
                  'mt-8': isCardPaymentType,
                }
              )}
            >
              <span>Primary</span>
            </div>
          )}
          {isCardPaymentType && isExpired(card.expMonth, card.expYear) && (
            <div className="flex items-center text-button-sm text-primary-magenta sm:hidden">
              <IconError className="mr-8 h-20 w-20" />
              <span>Expired</span>
            </div>
          )}
        </div>
      </div>
      {isExpanded ? (
        <IconPlus
          data-testid="payment-method-collapse"
          className="ml-8 h-[26px] w-[26px] rotate-45 cursor-pointer hover:text-primary-magenta dark:hover:text-primary-pink"
          onClick={() => setIsExpanded(!isExpanded)}
        />
      ) : (
        (isCardPaymentType || (!isPrimary && numberOfPaymentMethods > 1)) && (
          <SecondaryButton
            as="button"
            className="max-w-[50px] self-start font-suisse-intl text-button-sm"
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            Edit
          </SecondaryButton>
        )
      )}
    </div>
  );
};

export default PaymentMethodHeader;
